import _ from 'lodash';

/**
 * Turns an ordered list of GPS fixes into a stop/trip timeline.
 *
 * The Equipment Map panel used to draw every fix as a numbered breadcrumb, which
 * for a stationary asset is just a pile of GPS jitter and, over a multi-day
 * window, thousands of overlapping points. Operators actually ask a narrative
 * question -- "where was this unit and when did it move from A to B" -- whose
 * answer is a handful of *stops* (dwells at a place) connected by *trips*
 * (moves between places), not a dot cloud.
 *
 * Stay-point detection (Li et al.): walk the fixes in time order, grow a cluster
 * while it stays within `stopRadiusMeters` of its anchor fix, and if that cluster
 * spans at least `minDwellMs` it is a stop. Fixes that are not part of any stop
 * are transit, and the run between two stops (endpoints included so the line
 * connects) is a trip. This is purely sequential-along-path, so it lives in the
 * client after the windowed fetch -- no geo index, no schema change.
 */

const EARTH_RADIUS_METERS = 6_371_000;
const DEGREES_HALF_CIRCLE = 180;

export const DEFAULT_STOP_RADIUS_METERS = 75;
export const DEFAULT_MIN_DWELL_MS = 5 * 60 * 1000;

/**
 * Great-circle distance between two `{ lat, lng }` points, in metres.
 *
 * @param {{lat:number, lng:number}} a first point
 * @param {{lat:number, lng:number}} b second point
 * @returns {number} distance in metres
 */
export function haversineMeters(a, b) {
  const toRad = deg => (deg * Math.PI) / DEGREES_HALF_CIRCLE;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const hav = (Math.sin(dLat / 2) ** 2) +
    ((Math.sin(dLng / 2) ** 2) * Math.cos(lat1) * Math.cos(lat2));
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(hav)));
}

/**
 * Normalises raw fixes to `{ lat, lng, ts }` (ts = epoch ms), dropping any whose
 * coordinates or timestamp are not finite, and sorting ascending by time so the
 * caller need not pre-sort.
 *
 * @param {Array<{lat:*, lng:*, date:*}>} fixes raw fixes
 * @returns {Array<{lat:number, lng:number, ts:number}>} clean, time-ordered points
 */
function normalisePoints(fixes) {
  return _(fixes)
    .map((fix) => {
      const lat = parseFloat(fix.lat);
      const lng = parseFloat(fix.lng);
      const ts = new Date(fix.date).getTime();
      return { lat, lng, ts };
    })
    .filter(({ lat, lng, ts }) => Number.isFinite(lat) && Number.isFinite(lng) && Number.isFinite(ts))
    .sortBy('ts')
    .value();
}

/**
 * @param {Array<{lat:number, lng:number, ts:number}>} points time-ordered points
 * @param {number} startIndex first index of the dwell (inclusive)
 * @param {number} endIndex last index of the dwell (inclusive)
 * @returns {object} a stop segment centred on the dwell's mean position
 */
function makeStop(points, startIndex, endIndex) {
  const members = points.slice(startIndex, endIndex + 1);
  const lat = _.meanBy(members, 'lat');
  const lng = _.meanBy(members, 'lng');
  return {
    type: 'stop',
    lat,
    lng,
    indexStart: startIndex,
    indexEnd: endIndex,
    startDate: new Date(points[startIndex].ts),
    endDate: new Date(points[endIndex].ts),
    durationMs: points[endIndex].ts - points[startIndex].ts,
    fixCount: members.length,
  };
}

/**
 * Finds the dwells in a time-ordered point list.
 *
 * @param {Array<{lat:number, lng:number, ts:number}>} points time-ordered points
 * @param {number} stopRadiusMeters cluster radius from the anchor fix
 * @param {number} minDwellMs minimum time span for a cluster to count as a stop
 * @returns {Array<object>} stop segments in chronological order
 */
function detectStops(points, stopRadiusMeters, minDwellMs) {
  const stops = [];
  const count = points.length;
  let i = 0;
  while (i < count) {
    let j = i + 1;
    while (j < count && haversineMeters(points[i], points[j]) <= stopRadiusMeters) {
      j += 1;
    }
    // points[i .. j-1] all lie within the radius of the anchor points[i].
    const lastIndex = j - 1;
    const dwellMs = points[lastIndex].ts - points[i].ts;
    if (lastIndex > i && dwellMs >= minDwellMs) {
      stops.push(makeStop(points, i, lastIndex));
      i = j;
    } else {
      i += 1;
    }
  }
  return stops;
}

/**
 * @param {Array<{lat:number, lng:number, ts:number}>} points time-ordered points
 * @param {number} startIndex first index of the trip (inclusive)
 * @param {number} endIndex last index of the trip (inclusive)
 * @returns {?object} a trip segment, or null if it has fewer than two points
 */
function makeTrip(points, startIndex, endIndex) {
  const path = points.slice(startIndex, endIndex + 1);
  if (path.length < 2) {
    return null;
  }
  let distanceMeters = 0;
  for (let idx = 1; idx < path.length; idx += 1) {
    distanceMeters += haversineMeters(path[idx - 1], path[idx]);
  }
  const from = path[0];
  const to = path[path.length - 1];
  return {
    type: 'trip',
    path: _.map(path, ({ lat, lng, ts }) => ({ lat, lng, date: new Date(ts) })),
    from: { lat: from.lat, lng: from.lng, date: new Date(from.ts) },
    to: { lat: to.lat, lng: to.lng, date: new Date(to.ts) },
    startDate: new Date(from.ts),
    endDate: new Date(to.ts),
    durationMs: to.ts - from.ts,
    distanceMeters,
    straightMeters: haversineMeters(from, to),
  };
}

/**
 * Builds the interleaved trip/stop segment list from the detected stops. A trip
 * spans from the end of one stop (its departure fix) to the start of the next
 * (its arrival fix) so the drawn line connects the two places; the runs before
 * the first stop and after the last stop are trips too. With no stops, the whole
 * window is a single trip.
 *
 * @param {Array<{lat:number, lng:number, ts:number}>} points time-ordered points
 * @param {Array<object>} stops detected stops
 * @returns {Array<object>} ordered stop/trip segments
 */
function buildSegments(points, stops) {
  const segments = [];
  if (!stops.length) {
    const trip = makeTrip(points, 0, points.length - 1);
    return trip ? [trip] : [];
  }
  let cursor = 0;
  _.forEach(stops, (stop) => {
    const trip = makeTrip(points, cursor, stop.indexStart);
    if (trip) {
      segments.push(trip);
    }
    segments.push(stop);
    cursor = stop.indexEnd;
  });
  const tailTrip = makeTrip(points, cursor, points.length - 1);
  if (tailTrip) {
    segments.push(tailTrip);
  }
  return segments;
}

/**
 * Segments raw GPS fixes into an ordered stop/trip timeline.
 *
 * @param {Array<{lat:*, lng:*, date:*}>} fixes raw fixes; need not be pre-sorted
 * @param {object} [opts] tuning
 * @param {number} [opts.stopRadiusMeters=75] dwell cluster radius
 * @param {number} [opts.minDwellMs=300000] minimum dwell to count as a stop (5 min)
 * @returns {{points:Array, stops:Array, trips:Array, segments:Array}} the timeline
 */
export function segmentTrips(fixes, opts = {}) {
  const {
    stopRadiusMeters = DEFAULT_STOP_RADIUS_METERS,
    minDwellMs = DEFAULT_MIN_DWELL_MS,
  } = opts;
  const points = normalisePoints(fixes);
  const stops = detectStops(points, stopRadiusMeters, minDwellMs);
  const segments = buildSegments(points, stops);
  return {
    points,
    stops,
    trips: _.filter(segments, { type: 'trip' }),
    segments,
  };
}
